import type { User } from "../../slices/authSlice"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { FiBriefcase, FiPlus } from "react-icons/fi"
import { RootState } from "../../store/store"
import SavedApplicantBox from "../trademark/applicant/SavedApplicantBox"
import PatentApplicantForm from "./PatentApplicantForm"
import InfoPopover from "../ui/InfoPopover"
import { useResetListener } from "../../hooks/useResetListener"

// Applicant list for the EP1001 form (SCRUM-55). Mirrors the shape of
// the shared Trademark/Design AddApplicantBox (same saved/edit/delete
// flow, same onChange contract) but drops the EUIPO person search and
// renders the patent-specific PatentApplicantForm.

type PatentApplicantSectionProps = {
  onChange?: (list: any[]) => void
  orderingParty?: Partial<User> | null
  initialData?: any[] | null
  disabled?: boolean
}

export default function PatentApplicantSection({
  onChange,
  initialData,
  orderingParty,
  disabled = false
}: PatentApplicantSectionProps) {
  const currentUser = useSelector((state: RootState) => state.auth.user)
  const user = orderingParty === undefined ? currentUser : orderingParty
  const hasCompany = Boolean(user?.company && user.company.trim().length > 0)
  const displayName = user
    ? `${user.title ? user.title + " " : ""}${user.firstName} ${user.lastName}`
    : ""

  const [savedApplicants, setSavedApplicants] = useState<any[]>(initialData || [])
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [newApplicantType, setNewApplicantType] = useState<"natural" | "legal">(
    "natural"
  )

  useEffect(() => {
    if (initialData) setSavedApplicants(initialData)
  }, [initialData])

  const emitChange = (list: any[]) => {
    const cleaned = list
      .filter((a) => !a.isDeleted)
      .map(({ isDeleted, ...rest }) => rest)
    onChange?.(cleaned)
  }

  useResetListener(() => {
    setSavedApplicants([])
    setEditingIndex(null)
    setIsFormOpen(false)
    emitChange([])
  })

  const handleSave = (formData: any) => {
    setSavedApplicants((prev) => {
      const updated =
        editingIndex !== null
          ? prev.map((a, i) => (i === editingIndex ? { ...a, ...formData } : a))
          : [...prev, formData]
      emitChange(updated)
      return updated
    })
    setIsFormOpen(false)
    setEditingIndex(null)
  }

  const currentApplicant = editingIndex !== null ? savedApplicants[editingIndex] : null
  const currentType = currentApplicant?.type ?? newApplicantType

  return (
    <div className="flex flex-col gap-6">
      <h3 className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
        Ordering Party
        <InfoPopover text="Applicant or representative of client in home country; postal delivery address and invoice recipient" />
      </h3>

      <div className="flex flex-col gap-3">
        {user && (
          <div className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e6edf5] text-dantzBlue">
                <FiBriefcase size={18} />
              </span>
              <div className="relative min-w-0 flex-1">
                <span className="absolute right-0 top-0 rounded-full bg-[#e6edf5] px-2 py-0.5 text-[10px] font-medium text-dantzBlue">
                  Auto-filled
                </span>
                {hasCompany && (
                  <p className="mt-0.5 truncate pr-16 text-sm font-medium text-gray-800">
                    {user.company}
                  </p>
                )}
                <p className="truncate pr-16 text-xs text-gray-600">{displayName}</p>
                {hasCompany ? (
                  <>
                    {user.companyStreet && (
                      <p className="truncate text-xs text-gray-600">
                        {user.companyStreet}
                      </p>
                    )}
                    <p className="truncate text-xs text-gray-600">
                      {user.companyCity ?? "-"}, {user.companyCountry ?? "-"}
                    </p>
                  </>
                ) : (
                  <>
                    {user.street && (
                      <p className="truncate text-xs text-gray-600">{user.street}</p>
                    )}
                    <p className="truncate text-xs text-gray-600">
                      {user.city ?? "-"}, {user.country ?? "-"}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {savedApplicants.map(
          (applicant, index) =>
            !applicant.isDeleted && (
              <SavedApplicantBox
                key={index}
                applicant={applicant}
                onEdit={() => {
                  setEditingIndex(index)
                  setIsFormOpen(true)
                }}
                onDelete={() =>
                  setSavedApplicants((prev) => {
                    const updated = prev.map((a, i) =>
                      i === index ? { ...a, isDeleted: true } : a
                    )
                    emitChange(updated)
                    return updated
                  })
                }
              />
            )
        )}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return
          setEditingIndex(null)
          setNewApplicantType("natural")
          setIsFormOpen(true)
        }}
        className="flex w-full flex-col items-center justify-center rounded-xl border-[1.5px] border-dashed border-gray-300 bg-gray-50/60 p-5 text-dantzBlue transition-colors hover:border-dantzBlue hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FiPlus size={20} />
        <span className="mt-1 text-sm font-medium">New Applicant</span>
        <span className="mt-0.5 text-[11px] text-gray-400">
          Add a natural person or legal entity
        </span>
      </button>

      {isFormOpen && (
        <div className="max-w-full space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          {editingIndex === null && (
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { value: "natural", label: "Natural person" },
                { value: "legal", label: "Legal entity" }
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                    newApplicantType === option.value
                      ? "border-dantzBlue bg-blue-50 text-dantzBlue"
                      : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    checked={newApplicantType === option.value}
                    onChange={() =>
                      setNewApplicantType(option.value as "natural" | "legal")
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
          )}

          <PatentApplicantForm
            type={currentType}
            initialValues={currentApplicant || undefined}
            onSave={handleSave}
            onCancel={() => {
              setIsFormOpen(false)
              setEditingIndex(null)
            }}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  )
}
